/**
 * @file waiterStats.service.ts
 * @description Dedicated service for querying personal waiter sales and service KPIs.
 * 
 * SECURITY DEFINER CONTRACT:
 * Queries the backend RPC get_waiter_personal_stats which strictly scopes to auth.uid()
 * and enforces venue owner permission flags (show_sales_amount, show_avg_ticket, etc.).
 */

import { supabase } from '../../../config/supabase.config';
import type { WaiterPersonalStats, WaiterStatsTimeframe } from '../../../types/waiter.types';

// In-memory cache for waiter personal stats (60s TTL) to prevent repeated RPC executions
const waiterStatsCache = new Map<string, { data: WaiterPersonalStats; timestamp: number }>();
const WAITER_STATS_CACHE_TTL_MS = 60 * 1000;

export const fetchWaiterPersonalStats = async (
  venueId: string,
  timeframe: WaiterStatsTimeframe = 'day'
): Promise<WaiterPersonalStats> => {
  const cacheKey = `${venueId}:${timeframe}`;
  const cached = waiterStatsCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < WAITER_STATS_CACHE_TTL_MS) {
    return cached.data;
  }

  const { data, error } = await supabase.rpc('get_waiter_personal_stats', {
    p_venue_id: venueId,
    p_timeframe: timeframe,
  });

  if (error) {
    console.error('[waiterStats.service] Failed to fetch waiter stats:', error);
    throw error;
  }

  const formatted: WaiterPersonalStats = {
    timeframe,
    timezone: data?.timezone || undefined,
    orders_count: Number(data?.orders_count) || 0,
    tables_served: Number(data?.tables_served) || 0,
    items_sold: Number(data?.items_sold) || 0,
    avg_service_minutes: data?.avg_service_minutes !== undefined ? Number(data?.avg_service_minutes) : undefined,
    sales_handled: data?.sales_handled !== null ? Number(data?.sales_handled) : null,
    average_ticket: data?.average_ticket !== null ? Number(data?.average_ticket) : null,
    recent_orders: Array.isArray(data?.recent_orders) ? data.recent_orders : [],
    prior_years_totals: Array.isArray(data?.prior_years_totals) ? data.prior_years_totals : [],
    permissions: data?.permissions || {
      show_sales_amount: true,
      show_avg_ticket: true,
      show_orders_count: true,
      show_tables_served: true,
      show_restaurant_total_revenue: false,
    },
  };

  waiterStatsCache.set(cacheKey, { data: formatted, timestamp: Date.now() });
  return formatted;
};
